import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./carousel.html";
import { createEvent } from '~/modules/analytics/analyticsHelper';
import { ElementType } from '~/modules/analytics/gtmEventTypes/constants';
import { IS_DEVELOPMENT } from '~/modules/config';
import { createUserActivity } from '~/modules/services/simplifiedTrialsService';
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import { generateMediaUrl } from '~/modules/utilities/asset_utils';
import { throwDevelopmentOrLog } from '~/modules/utilities/cross_env_utils';
const DEMO_ACTIVITY_TYPE = 'Demo';
const CAROUSEL_ACTIVITY_TYPE = 'Screenshot';
const IMAGE_CONTENT_LIMIT = 5;
const IMAGE_CONTENT_LIMIT_MOBILE = 2;
class Carousel extends LightningElement {
  constructor(...args) {
    super(...args);
    this.labels = void 0;
    this.mediaItems = null;
    this.providerLogo = '';
    this.isMobile = false;
    this.hasDemoLeadsEnabled = false;
    this.selectedItem = 0;
    // This must be initialized to 0 since it's used to populate tabindex in the carousel component
    this.showLightbox = false;
    this.initialLightboxItemPosition = -1;
    this.focusSelectedItem = false;
    this.loadAllContentImages = false;
    this.scrollEventHandler = null;
    this.hasDemo = false;
    // @ts-expect-error * Automated Type Error Fix
    this.generateMediaUrlVideo = (mediaId, videoType = 'YouTube', videoStart) => {
      switch (videoType) {
        case 'YouTube':
          return videoStart ? `https://www.youtube-nocookie.com/embed/${mediaId}?start=${videoStart}&autoplay=1` : `https://www.youtube-nocookie.com/embed/${mediaId}?autoplay=1`;
        case 'Vidyard':
          return `https://play.vidyard.com/${mediaId}?autoplay=1`;
        case 'Vimeo':
          return `https://player.vimeo.com/video/${mediaId}?autoplay=1`;
        case 'Wistia':
          return `https://fast.wistia.net/embed/iframe/${mediaId}?autoplay=1`;
        default:
          if (IS_DEVELOPMENT) {
            throw new Error(`Unknown video type '${videoType}'`);
          }
      }
      return '';
    };
  }
  renderedCallback() {
    if (this.focusSelectedItem) {
      this.handleFocusOnSelectedItem();
    }
    if (this.showLightbox) {
      this.handleFocusOnLightbox();
    }
    if (!this.loadAllContentImages) {
      // @ts-expect-error * Automated Type Error Fix
      this.scrollEventHandler = this.handleCarouselHorizontalScroll.bind(this);
      const carouselListElement = this.template.querySelector('.carousel');
      // @ts-expect-error * Automated Type Error Fix
      carouselListElement?.addEventListener('scroll', this.scrollEventHandler);
      this.template.querySelectorAll('wds-analytics-instrument').forEach(instrument => {
        instrument.addListenerToScrollEvent(carouselListElement);
      });
    }
  }
  get processedMediaItems() {
    const imageContentLimit = this.isMobile ? IMAGE_CONTENT_LIMIT_MOBILE : IMAGE_CONTENT_LIMIT;
    // @ts-expect-error * Automated Type Error Fix
    const carouselItems = this.mediaItems
    // @ts-expect-error * Automated Type Error Fix
    .filter(item => item.pluginType === 'carousel')
    // @ts-expect-error * Automated Type Error Fix
    .sort((a, b) => a.data.position - b.data.position)
    // @ts-expect-error * Automated Type Error Fix
    .map(({
      type,
      data
    }, index) => ({
      key: index,
      caption: data.caption,
      isInteractive: type !== 'Image',
      hasFooter: !!data.caption,
      position: data.position,
      src: type !== 'Image' ? this.generateMediaUrlVideo(data.mediaId, data.videoType, data.videoStart) : generateMediaUrl(data.mediaId),
      showLoadingImage: index < imageContentLimit || this.loadAllContentImages ? false : true,
      type,
      cardTabIndex: type !== 'Image' ? '-1' : '0'
    }));

    // @ts-expect-error * Automated Type Error Fix
    const demoItems = this.mediaItems
    // @ts-expect-error * Automated Type Error Fix
    .filter(item => item.pluginType === 'demo' && item.url != null)
    // @ts-expect-error * Automated Type Error Fix
    .map((item, index) => ({
      key: index + carouselItems.length,
      caption: '',
      isInteractive: true,
      hasFooter: false,
      position: -1,
      type: 'Demo',
      url: item.url,
      cardTabIndex: '-1'
    }));
    if (demoItems.length > 0) {
      this.hasDemo = true;
    }
    const merged = [...demoItems, ...carouselItems].map((item, index, array) => ({
      ...item,
      altText: item.altText || i18nStore.getState().i18n.listingDetails.carousel.fallbackAltTag({
        CURRENT_POSITION: index + 1,
        TOTAL_ITEMS: array.length
      }),
      key: index,
      selected: index === this.selectedItem
    }));
    return merged;
  }
  get lightboxItems() {
    return this.processedMediaItems.filter(item => item.type !== 'Demo').map((item, position) => ({
      ...item,
      position
    }));
  }
  // @ts-expect-error * Automated Type Error Fix
  handleClick(event) {
    this.selectedItem = parseInt(event.currentTarget.getAttribute('data-position'));
    const selectedCarouselItem = this.processedMediaItems.filter(item => item.selected)[0];
    if (selectedCarouselItem && selectedCarouselItem.type !== 'Demo') {
      const clickEventDetail = {
        eventType: 'click',
        elementType: ElementType.CAROUSEL,
        caption: selectedCarouselItem.caption
      };
      const clickEvent = createEvent(clickEventDetail);
      this.dispatchEvent(clickEvent);
    }
    this.handleCarouselItemAction();
  }
  handleCarouselItemAction() {
    const selectedCarouselItem = this.processedMediaItems.filter(item => item.selected)[0];
    if (selectedCarouselItem.type !== 'Demo') {
      let itemPosition = this.selectedItem;
      if (this.hasDemo) {
        itemPosition--;
      }
      this.showLightboxItem(itemPosition);
      createUserActivity(CAROUSEL_ACTIVITY_TYPE);
    } else {
      // If user has leads enabled, they will open the legacy lead collection page, which generates leads itself.
      if (!this.hasDemoLeadsEnabled) {
        createUserActivity(DEMO_ACTIVITY_TYPE);
      }
      if (selectedCarouselItem.url) {
        window.open(selectedCarouselItem.url, '_blank');
      } else {
        throwDevelopmentOrLog(new Error('Found empty URL for DEMO carousel item'));
      }
    }
  }
  handleCarouselHorizontalScroll() {
    this.loadAllContentImages = true;
    const carouselListElement = this.template.querySelector('.carousel');
    carouselListElement?.removeEventListener('scroll', this.scrollEventHandler);
  }
  selectPreviousContentItem() {
    this.selectedItem = this.selectedItem - 1 < 0 ? 0 : this.selectedItem - 1;
    this.focusSelectedItem = true;
  }
  selectNextContentItem() {
    this.selectedItem =
    // @ts-expect-error * Automated Type Error Fix
    this.selectedItem + 1 > this.mediaItems.length - 1 ? this.mediaItems.length - 1 : this.selectedItem + 1;
    this.focusSelectedItem = true;
  }
  handleFocusOnSelectedItem() {
    this.template.querySelector('li[aria-selected="true"]')?.focus();
  }
  handleFocusOnLightbox() {
    this.template.querySelector('ace-lightbox')?.focus();
  }
  showLightboxItem(itemPosition) {
    this.initialLightboxItemPosition = itemPosition;
    this.toggleLightbox();
  }
  toggleLightbox() {
    this.showLightbox = !this.showLightbox;
  }

  // @ts-expect-error * Automated Type Error Fix
  handleImageShown(event) {
    const positionIndex = event.detail.positionIndex;
    event.detail.image = {
      positionIndex
    };
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(Carousel, {
  publicProps: {
    mediaItems: {
      config: 0
    },
    providerLogo: {
      config: 0
    },
    isMobile: {
      config: 0
    },
    hasDemoLeadsEnabled: {
      config: 0
    }
  },
  wire: {
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: i18n => i18n.listingDetails.carousel
        };
      }
    }
  },
  fields: ["selectedItem", "showLightbox", "initialLightboxItemPosition", "focusSelectedItem", "loadAllContentImages", "scrollEventHandler", "hasDemo", "generateMediaUrlVideo"]
});
const __lwc_component_class_internal = _registerComponent(Carousel, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;